import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import {
  PropertyApplicationDto,
  PropertyApplicationFilterKey,
  PropertyApplicationStatus,
} from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { Observable, switchMap, tap } from 'rxjs';
import { EtxeEnumsNiceLabelUtils } from '../../../utils/etxe-enums-nice-label.utils';

export type PropertyApplicationColumnKey =
  | 'property'
  | 'owner'
  | 'status'
  | 'engagement.commissionRate'
  | 'financials.sellingPrice'
  | 'pendingAction'
  | 'appointment'
  | 'createdAt'
  | 'updatedAt';

@Directive()
export abstract class AbstractPropertyApplicationsDataTableComponent<T extends PropertyApplicationDto>
  extends BaseDataTableComponent<T, PropertyApplicationColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<PropertyApplicationColumnKey>[]>([
    { key: 'property', label: 'Bien', display: true },
    { key: 'owner', label: 'Propriétaire', display: true },
    { key: 'status', label: 'Statut de la démarche', display: true },
    { key: 'engagement.commissionRate', label: 'Honoraires proposés', display: true },
    { key: 'financials.sellingPrice', label: 'Prix du bien', display: true },
    { key: 'pendingAction', label: 'Action requise', display: true },
    { key: 'createdAt', label: 'Date de candidature', display: true },
    { key: 'updatedAt', label: 'Dernière mise à jour', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Voir le suivi',
      icon: 'arrow_forward',
      onClick: (application: PropertyApplicationDto) => this.onItemClicked(application as T),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<PropertyApplicationColumnKey>[]>([
    { column: PropertyApplicationFilterKey.Status, label: 'Statut' },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params) => {
          const status = params.getAll(PropertyApplicationFilterKey.Status);

          this.filterChanged({
            changed: true,
            filter: {
              [PropertyApplicationFilterKey.Status]: status.length ? status : [],
            },
          });
        }),
        switchMap(() => this.refresh()),
      )
      .subscribe();
  }

  abstract override onItemClicked(application: T, auxClick?: boolean): void;

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;

  override getDisplayForEntry(key: PropertyApplicationColumnKey, rawValue: string): string {
    if (key === PropertyApplicationFilterKey.Status) {
      return EtxeEnumsNiceLabelUtils.applicationStatus(rawValue as PropertyApplicationStatus);
    }
    return rawValue;
  }
}
